/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 21/6/2023
 */

// HomePage.js

import React, { useState, useRef, useCallback, useEffect } from "react";
import { IconButton, Typography } from "@mui/material";
import { TrendingUp, Close, DragIndicator } from "@mui/icons-material";
import RightSidePanel from "../Components/Home/RightSidePanel";
import HomePanel from "../Components/Home/HomePanel";
import ActivityPanel from "../Components/Home/ActivityPanel";
import CustomizableWorkspace from "../Components/Restaurant/CustomizableWorkspace";
import SalesSummaryOverlay from "../Components/Shared/SalesSummaryOverlay";
import styles from "./HomePage.module.css";

const HomePage = () => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [showStats, setShowStats] = useState(true);
    const [statsPos, setStatsPos] = useState({ x: null, y: null });
    const [isMobileView, setIsMobileView] = useState(false);
    const [isSaleOpenMobile, setIsSaleOpenMobile] = useState(false);
    const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 640px)");

        const updateLayoutMode = () => {
            const mobile = mediaQuery.matches;
            setIsMobileView(mobile);
            setIsSaleOpenMobile(!mobile);
        };

        updateLayoutMode();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", updateLayoutMode);
            return () => mediaQuery.removeEventListener("change", updateLayoutMode);
        }

        mediaQuery.addListener(updateLayoutMode);
        return () => mediaQuery.removeListener(updateLayoutMode);
    }, []);

    useEffect(() => {
        const handleInvoiceCompleted = () => {
            if (window.matchMedia("(max-width: 640px)").matches) {
                setIsSaleOpenMobile(false);
            }
        };

        window.addEventListener("pos:invoice-completed", handleInvoiceCompleted);
        return () => {
            window.removeEventListener("pos:invoice-completed", handleInvoiceCompleted);
        };
    }, []);

    const handleItemClick = (item) => {
        // Check if the item is already in the selected items list
        const existingItem = selectedItems.find((selectedItem) => selectedItem.prodDefId === item.prodDefId);
        if (existingItem) {
            // If the item already exists, do nothing
            return;
        }
    
        // Set initial quantity to 1 when adding the item
        const itemWithQuantity = { ...item, quantity: 1 };
        setSelectedItems((prevSelectedItems) => [...prevSelectedItems, itemWithQuantity]);
    };
    
    const updateSelectedItems = (updatedItems) => {
        setSelectedItems(updatedItems);
      };

    const salesStats = [
        { label: "Today's Sales", value: "1,250.00", trend: "+12.5%" },
        { label: "Transactions", value: "32", trend: "+8.2%" },
        { label: "Avg. Sale", value: "39.06", trend: "+5.7%" },
        { label: "Items Sold", value: "112", trend: "+10.1%" },
    ];

    const onDragMove = useCallback((e) => {
        if (!dragRef.current.dragging) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setStatsPos({
            x: clientX - dragRef.current.offsetX,
            y: clientY - dragRef.current.offsetY,
        });
    }, []);

    const onDragEnd = useCallback(() => {
        dragRef.current.dragging = false;
        document.removeEventListener("mousemove", onDragMove);
        document.removeEventListener("mouseup", onDragEnd);
        document.removeEventListener("touchmove", onDragMove);
        document.removeEventListener("touchend", onDragEnd);
    }, [onDragMove]);

    const onDragStart = (e) => {
        const rect = e.currentTarget.parentElement.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        dragRef.current = {
            dragging: true,
            offsetX: clientX - rect.left,
            offsetY: clientY - rect.top,
        };
        document.addEventListener("mousemove", onDragMove);
        document.addEventListener("mouseup", onDragEnd);
        document.addEventListener("touchmove", onDragMove);
        document.addEventListener("touchend", onDragEnd);
    };

    const floatingStyle = statsPos.x !== null
        ? { left: `${statsPos.x}px`, top: `${statsPos.y}px`, right: "auto", bottom: "auto", transform: "none" }
        : undefined;

    const widgets = [
        {
            key: "products",
            title: "Products",
            node: (
                <div className={styles.mainPanelCard}>
                    <HomePanel
                        selectedItems={selectedItems}
                        handleItemClick={handleItemClick}
                    />
                </div>
            ),
            defaultSpan: 8,
            defaultDock: "left",
            visible: true,
        },
        {
            key: "sale",
            title: "Current Sale",
            node: (
                <RightSidePanel
                    selectedItems={selectedItems}
                    updateSelectedItems={updateSelectedItems}
                />
            ),
            defaultSpan: 4,
            defaultDock: "right",
            visible: true,
        },
    ];

    const presets = [
        {
            key: "three-column",
            label: "3 Columns",
            layout: {
                products: { order: 0, span: 6, dock: "left" },
                sale: { order: 1, span: 4, dock: "middle" },
                activity: { order: 2, span: 3, dock: "right" },
            },
        },
        {
            key: "products-sale",
            label: "Products + Sale",
            layout: {
                products: { order: 0, span: 8, dock: "left" },
                sale: { order: 1, span: 4, dock: "right" },
                activity: { order: 2, span: 12, dock: "top" },
            },
        },
    ];

    return (
        <div className={styles.dashboardShell}>
            {isMobileView ? (
                <div className={styles.workspaceRow}>
                    <div className={styles.mainColumn}>
                        <div className={styles.mainPanelCard}>
                            <HomePanel
                                selectedItems={selectedItems}
                                handleItemClick={handleItemClick}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className={styles.workspaceRow}>
                    <div className={styles.mainColumn}>
                        <CustomizableWorkspace
                            storageKey="posSupermarketLayout"
                            widgets={widgets}
                            presets={presets}
                            summary={<SalesSummaryOverlay variant="inline" stats={salesStats} />}
                        />
                    </div>
                    <div className={styles.activityColumn}>
                        <ActivityPanel />
                    </div>
                </div>
            )}

            {isMobileView && (
                <div className={styles.mobileSaleDock}>
                    <RightSidePanel
                        selectedItems={selectedItems}
                        updateSelectedItems={updateSelectedItems}
                        mobileCompact
                        mobileExpanded={false}
                        onMobileToggle={() => setIsSaleOpenMobile((prev) => !prev)}
                    />
                </div>
            )}

            {isMobileView && isSaleOpenMobile && (
                <div className={styles.mobileSaleSheet}>
                    <RightSidePanel
                        selectedItems={selectedItems}
                        updateSelectedItems={updateSelectedItems}
                        mobileSheet
                    />
                </div>
            )}

            {showStats && isMobileView && (
                <div className={styles.floatingStats} style={floatingStyle}>
                    <div
                        className={styles.floatingDragHandle}
                        onMouseDown={onDragStart}
                        onTouchStart={onDragStart}
                    >
                        <DragIndicator fontSize="small" />
                    </div>
                    {salesStats.map((stat) => (
                        <div key={stat.label} className={styles.statCard}>
                            <Typography className={styles.statLabel}>{stat.label}</Typography>
                            <div className={styles.statValueRow}>
                                <Typography className={styles.statValue}>{stat.value}</Typography>
                                <span className={styles.statTrend}>
                                    <TrendingUp fontSize="inherit" />
                                    {stat.trend}
                                </span>
                            </div>
                        </div>
                    ))}
                    <IconButton
                        className={styles.floatingClose}
                        onClick={() => setShowStats(false)}
                        aria-label="Close stats"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </div>
            )}
        </div>
    );
};

export default HomePage;
